import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Lock, Loader2, ChevronDown, ChevronRight } from 'lucide-react'

/**
 * THE action control of a treatment card: ONE split button, not a row of them.
 *
 *   [ + เพิ่มรายการ Billing │ ▾ ]
 *
 * The left half is the default action — the thing staff do on almost every
 * visit. The caret opens every other shortcut. This shape is deliberate: the
 * shortcut list is going to keep growing, and a row that grows sideways pushes
 * the common action off its familiar spot and turns a card footer into a
 * toolbar. A split button holds the default still no matter how many presets
 * land behind it.
 *
 * An action:
 *   { key, label, icon, onClick|to, state?, meta?, write?, primary?, hint?, disabled?, busy?, danger? }
 *
 *   primary — the default half of the split button. Exactly one; the first
 *             action is used if none says so.
 *   meta    — right-aligned trailing text in the menu (a price, a payer).
 *   write   — WRITES to the visit, so it obeys the rule เพิ่มรายการ Billing
 *             already obeys (below). Default false = read-only.
 *   hint    — title text; on a `disabled` action this is WHY, and it is the only
 *             explanation the user gets, so write it in Thai.
 *   busy    — the write is in flight: spinner, click dead. A one-click shortcut
 *             MUST set this, or an impatient second click bills twice.
 *   divider — draw a rule above this item: it starts a new group (the documents
 *             after the billing shortcuts).
 *   items   — a list of actions behind this one (เอกสารอื่นๆ → ใบแจ้งหนี้, ใบส่งตัว…),
 *             shown as a flyout beside the menu. Hover opens it, and so does a
 *             click — a hover-only submenu cannot be reached on a touch screen.
 *
 * `status` is the inline result of the last action — "✓ เพิ่มแล้ว · ยกเลิก" —
 * shown beside the button rather than in a page-top banner, because that is
 * where the user is looking and the page may be scrolled far past the top.
 *
 * THE GUARD, one rule for every action in here:
 *
 *   !canWrite  → no write action renders at all (a doctor-only view). No note:
 *                nothing is missing from that user's point of view.
 *   locked     → the day is closed. Write actions are ABSENT, not disabled, and
 *                one note explains the absence for the whole control. Owner's
 *                rule: a button that opens a drawer only to say "you may not" is
 *                a dead end. The real refusal is server-side — functions/api/
 *                billing/_guard.js — and this only mirrors it.
 *
 * Read-only actions ignore both and always render.
 */
export function TreatmentActionBar({ actions = [], canWrite = false, locked = false, status = null, className = '' }) {
  const shown = actions.filter((a) => a && (!a.write || (canWrite && !locked)))
  const showLockNote = canWrite && locked && actions.some((a) => a?.write)
  if (!shown.length && !showLockNote && !status) return null

  const primary = shown.find((a) => a.primary) || shown[0]
  const rest = shown.filter((a) => a !== primary)

  return (
    <div className={`px-4 py-2 border-t border-slate-50 flex flex-wrap items-center gap-x-2 gap-y-1.5 ${className}`}>
      {primary && <SplitButton primary={primary} rest={rest} />}
      {status}
      {showLockNote && (
        <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-400">
          <Lock size={12} /> วันที่ปิดแล้ว — เพิ่มหรือแก้รายการได้เฉพาะ Admin
        </span>
      )}
    </div>
  )
}

// Flyout timing. OPEN: long enough that sweeping the pointer down the menu past
// เอกสารอื่นๆ does not pop it open. CLOSE: the grace period for the pointer to
// travel diagonally into the flyout across the rows in between — without it the
// flyout shuts the moment the pointer clips ใบรับรองแพทย์ on the way.
const OPEN_DELAY = 120
const CLOSE_DELAY = 250

function SplitButton({ primary, rest }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const [sub, setSub] = useState(null)   // { a, el, focus } — the item whose flyout is showing
  const caretRef = useRef(null)
  const timer = useRef(null)

  const clearTimer = () => { clearTimeout(timer.current); timer.current = null }
  const later = (ms, fn) => { clearTimer(); timer.current = setTimeout(fn, ms) }
  useEffect(() => clearTimer, [])

  // The menu is position:FIXED, not absolute. The card is `overflow-hidden` (it
  // clips the billing table's corners), so an absolutely-positioned menu would
  // be cut off at the card's edge — which is exactly where this button sits.
  // Fixed escapes the clip; the trade is that it must close when the page moves
  // under it, hence the scroll/resize listeners.
  useEffect(() => {
    if (!open) { clearTimer(); setSub(null); return }
    const close = () => setOpen(false)
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  function toggle() {
    if (open) { setOpen(false); return }
    const r = caretRef.current?.getBoundingClientRect()
    if (!r) return
    // Flip up when the viewport bottom is closer than the menu is tall — the
    // last card on a page is the common case, not the exception.
    const need = 56 + rest.length * 38
    const below = window.innerHeight - r.bottom
    setPos(below >= need
      ? { top: r.bottom + 6, left: r.left }
      : { bottom: window.innerHeight - r.top + 6, left: r.left })
    setOpen(true)
  }

  const drillable = (a) => a.items?.length > 0 && !a.disabled && !a.busy

  // Pointer onto a row: a drillable row opens its flyout after a beat; any other
  // row starts the close grace on a flyout that is already open.
  function hoverRow(a, el) {
    if (drillable(a)) {
      if (sub?.a.key === a.key) { clearTimer(); return }
      later(OPEN_DELAY, () => setSub({ a, el }))
    } else if (sub) {
      later(CLOSE_DELAY, () => setSub(null))
    } else {
      clearTimer()
    }
  }

  // Click or → : open at once. Keyboard also moves focus into the flyout.
  function drill(a, el, focus) {
    clearTimer()
    setSub({ a, el, focus })
  }

  function back() {
    const el = sub?.el
    setSub(null)
    el?.focus()
  }

  return (
    <>
      <span className="inline-flex items-stretch rounded-lg ring-1 ring-brand-200 bg-brand-50/60 overflow-hidden">
        <Half a={primary} className="px-2.5 py-1.5" />
        {rest.length > 0 && (
          <>
            <span className="w-px bg-brand-200/70" aria-hidden />
            <button ref={caretRef} type="button" onClick={toggle}
              aria-haspopup="menu" aria-expanded={open} aria-label="ปุ่มลัดอื่น"
              className="px-1.5 text-brand-700 hover:bg-brand-100 transition-colors">
              <ChevronDown size={14} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </button>
          </>
        )}
      </span>

      {open && pos && (
        <>
          {/* A backdrop rather than a document click listener: it cannot miss a
              click that lands on something which stops propagation. */}
          <div className="fixed inset-0 z-40" onMouseDown={() => setOpen(false)} />
          <div role="menu" style={pos}
            onMouseLeave={() => { if (sub) later(CLOSE_DELAY, () => setSub(null)) }}
            className="fixed z-50 min-w-[248px] rounded-xl bg-white shadow-xl shadow-slate-900/10 ring-1 ring-slate-200 py-1 animate-ss-pop-in">
            {rest.map((a, i) => (
              // Hover is read off this wrapper, not the button: a disabled
              // button fires no mouse events, and it still has to close a flyout.
              <div key={a.key} onMouseEnter={(e) => hoverRow(a, e.currentTarget.lastElementChild)}>
                {a.divider && i > 0 && <div className="my-1 border-t border-slate-100" />}
                <MenuItem a={a} onDone={() => setOpen(false)}
                  expanded={sub?.a.key === a.key}
                  onDrill={drillable(a) ? (el, focus) => drill(a, el, focus) : undefined} />
              </div>
            ))}
          </div>
          {sub && (
            <Flyout sub={sub} onEnter={clearTimer}
              onLeave={() => later(CLOSE_DELAY, () => setSub(null))}
              onBack={back} onDone={() => setOpen(false)} />
          )}
        </>
      )}
    </>
  )
}

// The submenu, beside the row that opened it: to the right, or to the left when
// the right edge is too close; nudged up when it would run off the bottom. It is
// measured before paint (layout effect), so it never flashes in the wrong place.
function Flyout({ sub, onEnter, onLeave, onBack, onDone }) {
  const ref = useRef(null)
  const [style, setStyle] = useState({ top: 0, left: 0, visibility: 'hidden' })

  useLayoutEffect(() => {
    const el = ref.current
    const r = sub.el?.getBoundingClientRect()
    if (!el || !r) return
    const M = 8, GAP = 4
    const w = el.offsetWidth, h = el.offsetHeight
    let left = r.right + GAP
    if (left + w > window.innerWidth - M) left = Math.max(M, r.left - w - GAP)
    let top = r.top - 4   // first flyout row level with the parent row (py-1)
    if (top + h > window.innerHeight - M) top = Math.max(M, window.innerHeight - M - h)
    setStyle({ top, left })
    if (sub.focus) el.querySelector('[role=menuitem]:not([disabled])')?.focus()
  }, [sub])

  return (
    <div ref={ref} role="menu" aria-label={sub.a.label} style={style}
      onMouseEnter={onEnter} onMouseLeave={onLeave}
      onKeyDown={(e) => { if (e.key === 'ArrowLeft') { e.preventDefault(); onBack() } }}
      className="fixed z-50 min-w-[248px] rounded-xl bg-white shadow-xl shadow-slate-900/10 ring-1 ring-slate-200 py-1">
      {sub.a.items.map((a) => <MenuItem key={a.key} a={a} onDone={onDone} />)}
    </div>
  )
}

// The default half. A Link when the action navigates, a button when it acts.
function Half({ a, className }) {
  const dead = a.disabled || a.busy
  const Icon = a.busy ? Loader2 : a.icon
  const cls = `inline-flex items-center gap-1.5 text-[12.5px] font-medium transition-colors ${className} ${
    dead ? 'text-brand-300 cursor-not-allowed' : 'text-brand-700 hover:bg-brand-100'}`
  const body = <>{Icon && <Icon size={13} className={a.busy ? 'animate-spin' : undefined} />}{a.label}</>
  if (a.to && !dead) return <Link to={a.to} state={a.state} title={a.hint} className={cls}>{body}</Link>
  return <button type="button" onClick={a.onClick} disabled={dead} title={a.hint} className={cls}>{body}</button>
}

function MenuItem({ a, onDone, onDrill, expanded = false }) {
  const dead = a.disabled || a.busy
  const Icon = a.busy ? Loader2 : a.icon
  const cls = `w-full flex items-center gap-2.5 px-3 py-2 text-[13.5px] text-left transition-colors ${
    dead ? 'text-slate-300 cursor-not-allowed'
      : a.danger ? 'text-rose-600 hover:bg-rose-50'
        : expanded ? 'bg-brand-50 text-brand-700'
          : 'text-slate-700 hover:bg-brand-50 hover:text-brand-700'}`
  const body = (
    <>
      {Icon && <Icon size={14} className={`flex-none ${a.busy ? 'animate-spin' : ''}`} />}
      <span className="flex-1 truncate">{a.label}</span>
      {a.meta && <span className={`flex-none tabular-nums text-[12.5px] ${dead ? 'text-slate-300' : 'text-slate-400'}`}>{a.meta}</span>}
      {a.items?.length > 0 && <ChevronRight size={14} className={`flex-none ${dead ? 'text-slate-300' : 'text-slate-400'}`} />}
    </>
  )
  if (onDrill) {
    return (
      <button type="button" role="menuitem" aria-haspopup="menu" aria-expanded={expanded} title={a.hint}
        onClick={(e) => onDrill(e.currentTarget, false)}
        onKeyDown={(e) => { if (e.key === 'ArrowRight') { e.preventDefault(); onDrill(e.currentTarget, true) } }}
        className={cls}>{body}</button>
    )
  }
  if (a.to && !dead) {
    return <Link to={a.to} state={a.state} title={a.hint} onClick={onDone} className={cls} role="menuitem">{body}</Link>
  }
  return (
    <button type="button" role="menuitem" disabled={dead} title={a.hint}
      onClick={() => { onDone(); a.onClick?.() }} className={cls}>{body}</button>
  )
}
